import Skeleton from "react-loading-skeleton";

import { PaymentsPrice } from "~/api/Api";
import { useBillingAccess } from "~/contexts/BillingAccessContext";
import { useTeam } from "~/contexts/TeamProvider";
import { formatPrice } from "~/helpers/helpers";
import { useStagedBoolean } from "~/hooks/useStagedBoolean";

import styles from "./AutoptoTrialTerms.module.scss";

const BONUSES = [
  "AEO Visibility Engine",
  "Interlinking Tool",
  "AEO Instructions Module",
  "Schema Automation",
  "Link Building Triplex",
  "OnPage Automations",
];

// Whole dollars stay whole ($30), sub-cent unit prices keep their digits
// ($0.015), like the invoice table. Amounts are in cents, per batch of
// `divide_by` units.
const unitPrice = (price: PaymentsPrice) =>
  formatPrice(price.amount / (price.divide_by || 1), {
    input: "cents",
    min: 0,
    max: 6,
  });

/**
 * The usage pricing after the trial, from the team's own subscription
 * (the Site, Page and Keyword add-ons) and its plan allowances.
 */
const TrialPricing = () => {
  const { team } = useTeam();
  const { subscription, isLoading } = useBillingAccess();
  const isPending = subscription === undefined && isLoading;
  const showSkeleton = useStagedBoolean(isPending, 300, 400);

  const addonPrice = (key: string) =>
    subscription?.subscription_addons?.find(
      (addon) => addon.price.product?.key === key,
    )?.price;

  const sitePrice = addonPrice("site_max");
  const pagePrice = addonPrice("page_max");
  const keywordPrice = addonPrice("keyword_max");

  const product = team?.subscription?.price?.product;
  const included = [
    product?.page_max_included ? `${product.page_max_included} pages` : null,
    product?.keyword_max_included
      ? `${product.keyword_max_included} keywords`
      : null,
  ].filter((part): part is string => part !== null);

  const interval = sitePrice?.recurring_interval;
  const afterTrial = [
    sitePrice &&
      `After your trial, ${unitPrice(sitePrice)}${
        interval === "month" || interval === "year" ? `/${interval}` : ""
      } per site.`,
    included.length > 0 && `Includes ${included.join(" and ")} per site.`,
  ].filter(Boolean);

  const flex = [
    pagePrice && `Flex pages billed at ${unitPrice(pagePrice)} per page.`,
    keywordPrice && `Flex keywords at ${unitPrice(keywordPrice)} per keyword.`,
  ].filter(Boolean);

  // "Pay $0 today" from the start; without an answer the prices are left
  // out.
  return (
    <p className={styles.text}>
      <strong>Pay $0 today.</strong> {showSkeleton && <Skeleton count={2} />}
      {!isPending && (
        <>
          {afterTrial.join(" ")}
          {flex.length > 0 && (
            <>
              <br />
              {flex.join(" ")}
            </>
          )}
        </>
      )}
    </p>
  );
};

/**
 * What a card means during an Autopto trial, above Stripe's card form
 * (Kyle's wording). Stripe adds its own "You won't be charged today" line
 * above the Save button: it comes from the backend.
 */
const AutoptoTrialTerms = () => {
  const { team } = useTeam();

  const title = team?.platform_name
    ? `Add a Card to Launch the ${team.platform_name} Platform`
    : "Add a Card to Launch Your Platform";

  return (
    <section className={styles.terms}>
      {/* The dialog's name (Modal's aria-labelledby). */}
      <h2 id="unstyled-modal-title" className={styles.title}>
        {title}
      </h2>
      <p className={styles.subtitle}>
        No base subscription fees. Cancel anytime. Pay only for client usage.
      </p>
      <TrialPricing />
      <div>
        <p className={styles.bonusesTitle}>Plus, these bonuses:</p>
        <ul className={styles.bonuses}>
          {BONUSES.map((bonus) => (
            <li key={bonus}>
              <strong className={styles.green}>FREE</strong> {bonus}
            </li>
          ))}
          {/* Kyle: the whole line in green. */}
          <li className={styles.green}>
            30 Day No-Questions-Asked Money Back Guarantee
          </li>
        </ul>
      </div>
    </section>
  );
};

export default AutoptoTrialTerms;
